import type { GetParticipantsParams } from "@frontend/shared/features/participants/domain/types/participant.types";
import type {
  EventIdPublic,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";

export const PARTICIPANTS_QUERY_KEYS = {
  all: ["participants"] as const,

  /** Prefix for paginated list caches only. Invariant: only `byParams` writes under this prefix, so `getQueriesData<PaginatedParticipantList<…>>({ queryKey: list })` is sound. The explicit `"list"` segment follows a fixed discriminator position (index 1) that never holds a user-supplied id, so no event slug can collide. */
  list: ["participants", "list"] as const,

  byParams: (params: GetParticipantsParams) =>
    [...PARTICIPANTS_QUERY_KEYS.list, params] as const,

  me: (eventIdPublic: EventIdPublic) =>
    ["participants", "event", eventIdPublic, "me"] as const,

  byId: (
    eventIdPublic: string,
    participantIdPublic: EventParticipantIdPublic,
  ) =>
    [
      "participants",
      "event",
      eventIdPublic,
      "participant",
      participantIdPublic,
    ] as const,

  preferences: (participantIdPublic: EventParticipantIdPublic) =>
    ["participants", "preferences", participantIdPublic] as const,

  /** Key for participant invitation history (host-only). Uses the same explicit `"event"`/`"participant"` discriminators as `byId` so the key cannot collide with the `list` prefix regardless of eventIdPublic value. */
  invitationHistory: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) =>
    [
      "participants",
      "event",
      eventIdPublic,
      "participant",
      participantIdPublic,
      "invitations",
    ] as const,

  /** Key for participant activity log (host-only). Uses the same explicit `"event"`/`"participant"` discriminators as `byId` so the key cannot collide with the `list` prefix regardless of eventIdPublic value. */
  activityLog: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
    limit?: number,
  ) =>
    [
      "participants",
      "event",
      eventIdPublic,
      "participant",
      participantIdPublic,
      "activity",
      limit ?? null,
    ] as const,

  milestones: (
    eventIdPublic: EventIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) =>
    [
      "participants",
      "event",
      eventIdPublic,
      "participant",
      participantIdPublic,
      "milestones",
    ] as const,
} as const;
